"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.coverageBadgeText = exports.categoryCoverage = exports.coverageFromCounts = exports.coverageRowLabel = exports.formatLastSeen = exports.coverageToCardStatus = exports.sensorCoverageState = void 0;
const sensorInclusion_1 = require("./sensorInclusion");
const sensorOfflineAlert_1 = require("./sensorOfflineAlert");
const Sensor_1 = require("../types/Sensor");
const SensorAlert_1 = require("../types/SensorAlert");
/**
 * Single decision point for coverage. Wraps `getSensorInstallState` rather
 * than re-deriving the offline-vs-never-paired split — that split is subtle
 * (INACTIVE alone cannot tell them apart, because the sweep also ages
 * never-paired PENDING rows into INACTIVE) and having two implementations of
 * it is how the two surfaces drifted in the first place.
 */
function sensorCoverageState(sensor, nowSeconds) {
    // Checked ahead of the install state, which folds this row into
    // 'not_installed' along with the aged-out never-paired ones.
    if (String(sensor.status ?? '').toUpperCase() === Sensor_1.SensorStatus.PairedAwaitingData) {
        return 'awaiting_data';
    }
    const install = (0, sensorInclusion_1.getSensorInstallState)(sensor, nowSeconds);
    if (install === 'active')
        return 'covered';
    if (install === 'offline')
        return 'offline';
    return 'never_paired';
}
exports.sensorCoverageState = sensorCoverageState;
function coverageToCardStatus(coverage) {
    switch (coverage) {
        case 'covered':
            return SensorAlert_1.SensorCardStatus.Good;
        case 'offline':
            return SensorAlert_1.SensorCardStatus.Offline;
        case 'never_paired':
        case 'awaiting_data':
            return SensorAlert_1.SensorCardStatus.Unknown;
    }
}
exports.coverageToCardStatus = coverageToCardStatus;
const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
/**
 * Relative last-seen, for the gray sensor-row label. Returns `undefined` when
 * there is no history at all — that absence is exactly what distinguishes
 * "offline" from "never paired", so callers must not substitute a zero here.
 */
function formatLastSeen(lastReportedSeconds, nowSeconds) {
    if (!lastReportedSeconds || lastReportedSeconds <= 0)
        return undefined;
    const now = nowSeconds ?? Math.floor(Date.now() / 1000);
    const elapsed = now - lastReportedSeconds;
    if (elapsed < MINUTE)
        return 'just now';
    if (elapsed < HOUR)
        return `${Math.floor(elapsed / MINUTE)}m ago`;
    if (elapsed < DAY)
        return `${Math.floor(elapsed / HOUR)}h ago`;
    const days = Math.floor(elapsed / DAY);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}
exports.formatLastSeen = formatLastSeen;
/** The sensor's own row label. Gray channel — never red, never amber. */
function coverageRowLabel(coverage, lastReportedSeconds, nowSeconds) {
    if (coverage === 'covered')
        return undefined;
    if (coverage === 'awaiting_data')
        return 'Awaiting data';
    if (coverage === 'never_paired')
        return 'Never paired';
    const seen = formatLastSeen(lastReportedSeconds, nowSeconds);
    return seen ? `Offline · last seen ${seen}` : 'Offline';
}
exports.coverageRowLabel = coverageRowLabel;
/**
 * 'partial' de-greens the card (neutral surface + amber coverage badge);
 * 'none' escalates the headline value itself to "Not monitored".
 */
function coverageFromCounts(impairedCount, sensorCount) {
    if (impairedCount <= 0)
        return 'full';
    if (sensorCount > 0 && impairedCount >= sensorCount)
        return 'none';
    return 'partial';
}
exports.coverageFromCounts = coverageFromCounts;
/** Category roll-up. Drives the amber coverage badge and "Not monitored". */
function categoryCoverage(summaries) {
    let offlineCount = 0;
    let unknownCount = 0;
    summaries.forEach((s) => {
        if (s.coverage === 'offline')
            offlineCount++;
        else if (s.coverage === 'never_paired' || s.coverage === 'awaiting_data')
            unknownCount++;
    });
    return {
        offlineCount,
        unknownCount,
        coverage: coverageFromCounts(offlineCount + unknownCount, summaries.length),
    };
}
exports.categoryCoverage = categoryCoverage;
function coverageBadgeText(params) {
    const { offlineCount, unknownCount, sensorCount, unitLabel = 'Sensor', sensors, worstSensor, format = 'stake', } = params;
    const impaired = offlineCount + unknownCount;
    if (impaired === 0)
        return undefined;
    // "all 3 Systems" pluralises by the impaired count; "1 of 3 Sensors" by the
    // roster the "of" refers to — otherwise a lone dark sensor read "1 of 3 Sensor".
    const units = `${unitLabel}${impaired === 1 ? '' : 's'}`;
    const ofUnits = `${unitLabel}${sensorCount === 1 ? '' : 's'}`;
    const allDown = sensorCount > 0 && impaired >= sensorCount;
    const impairedSensors = sensors?.filter((s) => s.coverage && s.coverage !== 'covered');
    // A just-paired sensor is an install in progress, not a gap in coverage —
    // answer before any of the "offline" / "Not monitoring" copy below.
    if (impairedSensors &&
        impairedSensors.length > 0 &&
        impairedSensors.every((s) => s.coverage === 'awaiting_data')) {
        return format === 'short'
            ? 'Awaiting data'
            : 'Awaiting data — first report not in yet';
    }
    const single = impaired === 1
        ? impairedSensors && impairedSensors.length === 1
            ? impairedSensors[0]
            : worstSensor
        : undefined;
    if (format === 'short') {
        if (single)
            return (0, sensorOfflineAlert_1.offlineShortLine)(single.sensorType, single.location, single.name);
        if (allDown)
            return `All ${impaired} ${units} offline`;
        return `${impaired} of ${sensorCount} ${ofUnits} offline`;
    }
    if (single) {
        return `Not monitoring — ${(0, sensorOfflineAlert_1.coverageStakeLine)(single.sensorType, single.location, single.name)}`;
    }
    if (allDown) {
        return `Not monitoring — all ${impaired} ${units} offline`;
    }
    return `Not monitoring — ${impaired} of ${sensorCount} ${ofUnits}`;
}
exports.coverageBadgeText = coverageBadgeText;
